/**
 * OT Request form — reusable for create, view, and edit (detail page).
 * Spec: workDate, startTime, endTime (hours auto-calculated), reason optional.
 * When canSelectEmployee=true (user has approve/list), optional employeeId to create request for another employee.
 */
import type { OvertimeRequestDetailDTO } from "@/modules/hr/overtime/domain/types";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import type { LookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormInput,
  FormRoot,
  FormTextarea,
  toast,
  zodResolver,
} from "@devhop/ui";
import { ArrowLeft, Clock, Send, Sparkles, User } from "lucide-react";
import * as RHF from "react-hook-form";
import { useMemo, useState } from "react";
import { z } from "zod";
import { otRequestFormSamples } from "./otRequestFormSamples";

const OTRequestFormSchema = z.object({
  workDate: z.string().min(1, "ກະລຸນາເລືອກວັນທີ່ເຮັດວຽກ"),
  startTime: z.string().min(1, "ກະລຸນາເລືອກເວລາເລີ່ມຕົ້ນ"),
  endTime: z.string().min(1, "ກະລຸນາເລືອກເວລາສິ້ນສຸດ"),
  reason: z.string(),
  /** เมื่อ canSelectEmployee = true: เลือกพนักงานที่ต้องการสร้างคำขอให้ (ว่าง = สຳລັບຕົວເອງ) */
  employeeId: z.string().optional(),
});

export type OTRequestFormValues = {
  workDate: string;
  startTime: string;
  endTime: string;
  reason: string;
  employeeId?: string;
};

function parseHHmm(s: string): number {
  const parts = String(s).trim().split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return h + m / 60;
}

function computeHours(startTime: string, endTime: string): number {
  const start = parseHHmm(startTime);
  const end = parseHHmm(endTime);
  return Math.max(0, Math.round((end - start) * 100) / 100);
}

/** workDate from API may be "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss" */
function workDateToInput(workDate: string): string {
  if (!workDate) return "";
  return workDate.slice(0, 10);
}

export type OTRequestFormMode = "create" | "view" | "edit";

export type OTRequestFormProps = {
  mode: OTRequestFormMode;
  /** Prefill for view/edit */
  request?: OvertimeRequestDetailDTO | null;
  /** เมื่อ true แสดงตัวเลือกพนักงาน (สำหรับผู้มีสิทธิ์ approve/list) — ไม่เลือก = สร้างคำขอให้ตัวเอง */
  canSelectEmployee?: boolean;
  onSubmit: (values: OTRequestFormValues) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
};

const TIME_PRESETS: { label: string; start: string; end: string }[] = [
  { label: "18:00 – 21:00 (3h)", start: "18:00", end: "21:00" },
  { label: "18:00 – 22:00 (4h)", start: "18:00", end: "22:00" },
  { label: "08:00 – 12:00 (4h)", start: "08:00", end: "12:00" },
];

export function OTRequestForm({
  mode,
  request,
  canSelectEmployee = false,
  onSubmit,
  onCancel,
  isSubmitting,
}: OTRequestFormProps) {
  const isCreate = mode === "create";
  const isView = mode === "view";
  const readOnly = isView;

  const defaultValues: OTRequestFormValues = useMemo(() => {
    if (request) {
      return {
        workDate: workDateToInput(request.workDate),
        startTime: request.startTime,
        endTime: request.endTime,
        reason: request.reason ?? "",
        ...(canSelectEmployee ? { employeeId: "" } : {}),
      };
    }
    return {
      workDate: "",
      startTime: "18:00",
      endTime: "21:00",
      reason: "",
      ...(canSelectEmployee ? { employeeId: "" } : {}),
    };
  }, [request, canSelectEmployee]);

  const [sampleIndex, setSampleIndex] = useState(0);
  const isDev =
    typeof import.meta !== "undefined" &&
    (import.meta.hot !== undefined || import.meta.env?.MODE === "development");

  const methods = RHF.useForm<OTRequestFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(OTRequestFormSchema) as any,
    defaultValues,
    values: request
      ? {
          workDate: workDateToInput(request.workDate),
          startTime: request.startTime,
          endTime: request.endTime,
          reason: request.reason ?? "",
          ...(canSelectEmployee ? { employeeId: "" } : {}),
        }
      : undefined,
  });

  const startTime = RHF.useWatch({ control: methods.control, name: "startTime" }) ?? "";
  const endTime = RHF.useWatch({ control: methods.control, name: "endTime" }) ?? "";
  const computedHours = useMemo(
    () => (startTime && endTime ? computeHours(startTime, endTime) : 0),
    [startTime, endTime],
  );

  const applyPreset = (preset: (typeof TIME_PRESETS)[number]) => {
    if (readOnly) return;
    methods.setValue("startTime", preset.start);
    methods.setValue("endTime", preset.end);
  };

  const handleAutoFill = () => {
    const sample = otRequestFormSamples[sampleIndex];
    if (sample) {
      methods.setValue("workDate", new Date().toISOString().slice(0, 10));
      methods.setValue("startTime", sample.startTime);
      methods.setValue("endTime", sample.endTime);
      methods.setValue("reason", sample.reason);
      setSampleIndex((prev) => (prev + 1) % otRequestFormSamples.length);
      toast.success("ເພີ່ມຂໍ້ມູນຕົວຢ່າງແລ້ວ. ກະລຸນາແກ້ໄຂວັນທີເຮັດວຽກຕາມຄວາມເໝາະສົມ.");
    }
  };

  const handleValidSubmit = (values: OTRequestFormValues) => {
    const hours = computeHours(values.startTime, values.endTime);
    if (hours <= 0) {
      toast.error("ເວລາສິ້ນສຸດຕ້ອງຫຼາຍກວ່າເວລາເລີ່ມຕົ້ນ");
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (values.workDate > today) {
      toast.error("ວັນທີ່ເຮັດວຽກບໍ່ສາມາດເປັນວັນໃນອະນາຄົດ");
      return;
    }
    onSubmit(values);
  };

  return (
    <FormRoot<OTRequestFormValues> methods={methods} onSubmit={handleValidSubmit} className="w-full">
      {canSelectEmployee && isCreate && !readOnly && (
        <Card className="mb-4 w-full">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-4 w-4" />
              ສຳລັບພະນັກງານ
            </CardTitle>
            <p className="text-muted-foreground text-sm font-normal">
              ເລືອກພະນັກງານທີ່ຕ້ອງການສ້າງຄຳຂໍ OT ໃຫ້ (ບໍ່ເລືອກ = ສຳລັບຕົວເອງ)
            </p>
          </CardHeader>
          <CardContent>
            <FormInfiniteCombobox<LookupItem>
              name="employeeId"
              label=""
              queryKey={["users-lookup-ot-request"]}
              queryFn={(args) =>
                fetchLookupForInfinite(`${config.apiUrl}/users/lookup`, args)
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກພະນັກງານ... (ວ່າງ = ຕົວເອງ)"
            />
          </CardContent>
        </Card>
      )}

      <Card className="w-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4" />
            {isCreate ? "ຟອມຄຳຂໍ OT" : "ລາຍລະອຽດຄຳຂໍ OT"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormInput
            name="workDate"
            label="ວັນທີ່ເຮັດວຽກ (workDate)"
            type="date"
            requiredMark
            disabled={readOnly}
            {...(readOnly ? {} : { max: new Date().toISOString().slice(0, 10) })}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormInput
              name="startTime"
              label="ເວລາເລີ່ມຕົ້ນ (startTime)"
              type="time"
              requiredMark
              disabled={readOnly}
            />
            <FormInput
              name="endTime"
              label="ເວລາສິ້ນສຸດ (endTime)"
              type="time"
              requiredMark
              disabled={readOnly}
            />
          </div>

          {!readOnly && (
            <div className="flex flex-wrap gap-2">
              {TIME_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}

          <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
            <span className="text-muted-foreground">ຊົ່ວໂມງ (ຄິດໄລ່ອັດຕະໂນມັດ): </span>
            <span className="font-medium">{computedHours.toFixed(2)}</span> ຊົ່ວໂມງ
          </div>

          <FormTextarea
            name="reason"
            label="ເຫດຜົນ / ຫມາຍເຫດ (optional)"
            rows={3}
            disabled={readOnly}
            placeholder="ອະທິບາຍເພີ່ມເຕີມ..."
          />
        </CardContent>
      </Card>

      {/* Footer — sticky so save actions stay visible when scrolling */}
      <div className="sticky bottom-0 z-10 mt-8 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            <ArrowLeft className="h-4 w-4" />
            {readOnly ? "ກັບຄືນ" : "ຍົກເລີກ"}
          </Button>
          {isDev && isCreate && otRequestFormSamples.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAutoFill}
              disabled={isSubmitting}
              className="gap-1.5"
            >
              <Sparkles className="h-4 w-4" />
              ຕື່ມຕົວຢ່າງ ({sampleIndex + 1}/{otRequestFormSamples.length})
            </Button>
          )}
        </div>
        {!readOnly && (
          <Button type="submit" disabled={isSubmitting}>
            <Send className="h-4 w-4" />
            {isCreate ? "ສົ່ງຄຳຂໍ" : "ບັນທຶກການແກ້ໄຂ"}
          </Button>
        )}
      </div>
    </FormRoot>
  );
}
